/**
 * Field provenance for the review page (2026-09-15, option 2's line).
 *
 * The change history already records every staff correction: who, when, the
 * value before, the value after, and the note they gave. This turns that trail
 * into one line per field — the LAST staff change to each path — so the row
 * can say "Changed by Ana Reyes on Aug 3, 2026 from Chris Okafor" under the
 * value, in the reviewer's words, instead of leaving the family's original
 * number three screens down in the trail.
 *
 * Only `kind: 'field'` rows count: those are staff edits through the field
 * editor. A family's own submission, a document upload or a status change is
 * not a correction to a value and never earns a provenance line.
 */
import type { ChangeHistoryRow } from '@/types/applications';
import { formatPreset } from '@/i18n/intl';

export interface FieldProvenance {
  /** Who made the change, in words: a name, or "a removed user" when the
   *  account is gone (never blank — D-SE-21). */
  actor: string;
  /** ISO timestamp of the change, or null when the trail lost it. */
  at: string | null;
  /** The value the family (or the previous editor) had on file. */
  before: unknown;
  /** The reason the editor gave, or null when they gave none. */
  note: string | null;
}

function actorWords(actor: ChangeHistoryRow['actor']): string {
  if (actor.name) return actor.name;
  if (actor.missing) return 'a removed user';
  return 'TRF';
}

/** Index the trail by path → most recent staff change. Rows come newest first
 *  from the server, but the index does not rely on it: it keeps the latest
 *  `at` per path, and the first seen when timestamps tie or are missing. */
export function provenanceByPath(rows: readonly ChangeHistoryRow[] | undefined): ReadonlyMap<string, FieldProvenance> {
  const out = new Map<string, FieldProvenance>();
  for (const row of rows ?? []) {
    if (row.kind !== 'field') continue;
    for (const change of row.changes) {
      const existing = out.get(change.path);
      if (existing && (existing.at ?? '') >= (row.at ?? '')) continue;
      out.set(change.path, {
        actor: actorWords(row.actor),
        at: row.at,
        before: change.before,
        note: row.note,
      });
    }
  }
  return out;
}

/** "Aug 3, 2026" in the viewer's zone — dates staff cite, so no relative words. */
export function provenanceDate(at: string | null): string | null {
  if (!at) return null;
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return null;
  return formatPreset(d, 'monthDayYear');
}
